import { DIET_DEFAULT_DAYS_COUNT, DIET_WEEK_DAYS_COUNT } from '@/const'
import { groupMap } from '@/utils/common'
import { getDietDaysCount } from '@/utils/dietDays'

import type { Coordinates, DietIngredient, StoreDiet, StoreDietDish } from '@/types/Diet'

import type { ShoppingList, ShoppingListSource } from './types'

export const emptyShoppingList = (): ShoppingList => ({
  dishesAndIngredients: {},
  groups: [],
  ingredients: [],
})

const round = (value: number, precision: number) => {
  const factor = 10 ** precision
  return Math.round((value + Number.EPSILON) * factor) / factor
}

const normalized = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\u0142/g, 'l')

type BuildShoppingListOptions = {
  allergens: {
    allergies_id: number[]
    intolerances_id: number[]
    like_id: number[]
    not_like_id: number[]
  }
  diet?: StoreDiet
  getRelatedCoordinates: (mealIndex: number, dayIndex: number, itemIndex: number) => Coordinates[]
  listFor: string
  reverse: boolean
  sortBy: string
}

export const buildShoppingList = ({
  allergens,
  diet,
  getRelatedCoordinates,
  listFor,
  reverse,
  sortBy,
}: BuildShoppingListOptions) => {
  const list = emptyShoppingList()
  if (!diet) return list

  const addIngredient = (ingredient: DietIngredient, group: string) => {
    const id = ingredient.ingredient_id
    const quantity = ingredient.quantity
    const measurementQuantity = quantity / ingredient.grams
    const existing = list.ingredients.find(item => item.id === id)
    if (existing) {
      existing.quantity = round(existing.quantity + quantity, 1)
      existing.measurementQuantity = round(existing.measurementQuantity + measurementQuantity, 2)
      return
    }
    list.ingredients.push({
      allergies: allergens.allergies_id.includes(id),
      group,
      id,
      intolerances: allergens.intolerances_id.includes(id),
      like: allergens.like_id.includes(id),
      measurement: ingredient.measurement,
      measurementQuantity: round(measurementQuantity, 2),
      name: ingredient.name,
      notLike: allergens.not_like_id.includes(id),
      quantity: round(quantity, 1),
    })
  }
  const addSource = (
    item: StoreDietDish | DietIngredient,
    ingredient: DietIngredient,
    coordinates: Coordinates[],
  ) => {
    const sources = (list.dishesAndIngredients[ingredient.ingredient_id] ??= [])
    let source = sources.find(value => value.name === item.name)
    if (!source) {
      source = {
        days: Array.from({ length: getDietDaysCount(diet) || DIET_DEFAULT_DAYS_COUNT }, () => null),
        name: item.name,
      }
      sources.push(source)
    }
    for (const coordinate of coordinates) source.days[coordinate.dayIndex] ??= coordinate
  }

  const daysCount = diet.data.meals[0]?.days.length ?? 0
  for (let dayIndex = 0; dayIndex < daysCount; dayIndex++) {
    diet.data.meals.forEach((meal, mealIndex) => {
      ;(meal.days[dayIndex]?.dishes_and_ingredients ?? []).forEach((item, itemIndex) => {
        const coordinates = getRelatedCoordinates(mealIndex, dayIndex, itemIndex).sort(
          (a, b) =>
            a.dayIndex - b.dayIndex ||
            a.mealIndex - b.mealIndex ||
            a.dishOrIngredientIndex - b.dishOrIngredientIndex,
        )
        const first = coordinates[0]
        const firstEncounter =
          !first ||
          (first.dayIndex === dayIndex &&
            first.mealIndex === mealIndex &&
            first.dishOrIngredientIndex === itemIndex)
        const selectedWeek =
          listFor === 'all' || Number(listFor) === Math.floor(dayIndex / DIET_WEEK_DAYS_COUNT) + 1
        const ingredients = item.type === 'dish' ? item.ingredients : [item]
        ingredients.forEach(ingredient => {
          if (firstEncounter && selectedWeek && 'category' in ingredient) {
            const group = groupMap[ingredient.category]
            if (group) {
              if (!list.groups.includes(group)) list.groups.push(group)
              addIngredient(ingredient, group)
            }
          }
          addSource(item, ingredient, coordinates)
        })
      })
    })
  }

  list.groups.sort()
  list.ingredients.sort(
    sortBy === 'name'
      ? (a, b) => normalized(a.name).localeCompare(normalized(b.name))
      : (a, b) => a.quantity - b.quantity,
  )
  if (reverse) {
    list.ingredients.reverse()
    if (sortBy === 'name') list.groups.reverse()
  }
  return list
}

export const sortShoppingListSources = (sources: ShoppingListSource[], byName: boolean) =>
  byName
    ? [...sources].sort((a, b) => normalized(a.name).localeCompare(normalized(b.name)))
    : sources
